import { getErrorMonitoringProvider } from "./provider";
import { getResolvedDatadogSessionSampleRatesForBrowser } from "./datadog-sample-rate";
import { getDefaultAllowedTracingUrls, getMonitoringWhitelistUrls } from "./monitoring-urls";

const mergeClientOptions = (overrides = {}) => ({
  whitelistedUrls: getMonitoringWhitelistUrls(),
  allowedTracingUrls: getDefaultAllowedTracingUrls(),
  ...overrides,
});

/**
 * Next.js `instrumentation-client` navigation hook (provider-aware).
 * Lazy-loads SDKs so Node/server bundles do not pull @datadog/browser-rum.
 */
export function onRouterTransitionStart(...args) {
  if (typeof window === "undefined") {
    return undefined;
  }

  const provider = getErrorMonitoringProvider();

  if (provider === "datadog") {
    const { onRouterTransitionStart: datadogHook } = require("@datadog/browser-rum-nextjs");
    return datadogHook(...args);
  }

  if (provider === "sentry") {
    const { captureRouterTransitionStart } = require("@sentry/nextjs");
    return captureRouterTransitionStart?.(...args);
  }

  return undefined;
}

/**
 * Initialise browser monitoring (RUM / Sentry). Browser-only, idempotent.
 * Sync init (Datadog) so dd_sr cookie rates apply before onRouterTransitionStart can load the SDK.
 */
export function initMonitoringInstrumentationClient(options = {}) {
  if (typeof window === "undefined") {
    return null;
  }

  const provider = getErrorMonitoringProvider();
  const configOptions = mergeClientOptions(options);

  if (provider === "datadog") {
    const resolvedRates = getResolvedDatadogSessionSampleRatesForBrowser();

    const {
      generateDatadogInitialisationConfig,
      initialiseClient,
    } = require("@stadium/services/datadog-nextjs/config/client");
    return initialiseClient(
      generateDatadogInitialisationConfig({
        ...configOptions,
        sessionSampleRate: options.sessionSampleRate ?? resolvedRates.sessionSampleRate,
        sessionReplaySampleRate:
          options.sessionReplaySampleRate ?? resolvedRates.sessionReplaySampleRate,
      }),
    );
  }

  if (provider === "sentry") {
    const {
      generateSentryInitialisationConfig,
      initialiseClient,
    } = require("@stadium/services/sentry-nextjs/config/client");
    return initialiseClient(generateSentryInitialisationConfig(configOptions));
  }

  return null;
}

if (typeof window !== "undefined") {
  initMonitoringInstrumentationClient();
}
